import { createVuetify } from 'vuetify';
import '@mdi/font/css/materialdesignicons.css';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
//DragScroll
import { VueDraggableNext } from 'vue-draggable-next';

// VLabs Components
import { VTimePicker } from 'vuetify/labs/VTimePicker';
import { VTreeview } from 'vuetify/labs/VTreeview';

// import { BLUE_THEME} from '@/theme/LightTheme';
import { BLUE_THEME} from '@shared/theme/LightTheme';
import {
    DARK_BLUE_THEME,
    DARK_AQUA_THEME,
    DARK_ORANGE_THEME,
    DARK_PURPLE_THEME,
    DARK_GREEN_THEME,
    DARK_CYAN_THEME
} from '@shared/theme/DarkTheme';

// Vuetify locale 설정 - JSON 파일에서 로드
import { getVuetifyLocale } from '@/utils/locales/messages';

// datePicker 모듈의 JSON 파일들을 동적으로 로드
const datePickerTranslations = import.meta.glob('@/utils/locales/modules/datePicker/*.json', { eager: true });

// Vuetify locale 형식으로 변환
const vuetifyLocales: Record<string, any> = {};

Object.entries(datePickerTranslations).forEach(([path, module]) => {
    // 경로에서 언어 코드 추출 (예: './modules/datePicker/ko.json' -> 'ko')
    const match = path.match(/datePicker\/([^/]+)\.json$/);
    if (match) {
        const langCode = match[1].toLowerCase();
        const vuetifyLocale = getVuetifyLocale(langCode);

        if (vuetifyLocale) {
            // JSON 파일의 내용을 Vuetify locale 형식으로 변환
            const translationData = (module as any).default || module;
            vuetifyLocales[vuetifyLocale] = translationData;

            // en은 en-US와 en-GB 모두에 적용 (en-GB가 별도 파일이 없는 경우)
            if (langCode === 'en') {
                vuetifyLocales['en-GB'] = translationData;
            }
        }
    }
});

// 기본값 설정 (로드 실패 시 fallback)
if (!vuetifyLocales['en-US']) {
    vuetifyLocales['en-US'] = {
        datePicker: {
            title: 'Select Date',
            header: 'Select Date',
            ok: 'OK',
            cancel: 'Cancel',
            clear: 'Clear',
            prevMonth: 'Previous Month',
            nextMonth: 'Next Month',
            prevYear: 'Previous Year',
            nextYear: 'Next Year',
        },
        open: 'Open',
        close: 'Close',
        input: {
            clear: 'Clear'
        },
        noDataText: 'No data available',
        dataIterator: {
            loadingText: 'Loading...',
            noResultsText: 'No matching records found'
        },
        pagination: {
            ariaLabel: {
                root: 'Pagination Navigation',
                next: 'Go to next page',
                previous: 'Go to previous page',
                page: 'Go to page {0}',
                currentPage: 'Page {0}',
                first: 'Go to first page',
                last: 'Go to last page'
            }
        }
    };
}

// 모든 로케일에 dataIterator 번역 키 추가 (없는 경우)
Object.keys(vuetifyLocales).forEach(locale => {
    if (!vuetifyLocales[locale].dataIterator) {
        vuetifyLocales[locale].dataIterator = {
            loadingText: locale.startsWith('ko') ? '로딩 중...' : 'Loading...',
            noResultsText: locale.startsWith('ko') ? '일치하는 결과가 없습니다' : 'No matching records found'
        };
    }
});

// 모든 로케일에 pagination 번역 키 추가 (없는 경우)
Object.keys(vuetifyLocales).forEach(locale => {
    if (!vuetifyLocales[locale].pagination) {
        vuetifyLocales[locale].pagination = {
            ariaLabel: {
                root: locale.startsWith('ko') ? '페이지네이션 네비게이션' : 'Pagination Navigation',
                next: locale.startsWith('ko') ? '다음 페이지로 이동' : 'Go to next page',
                previous: locale.startsWith('ko') ? '이전 페이지로 이동' : 'Go to previous page',
                page: locale.startsWith('ko') ? '{0}페이지로 이동' : 'Go to page {0}',
                currentPage: locale.startsWith('ko') ? '{0}페이지' : 'Page {0}',
                first: locale.startsWith('ko') ? '첫 페이지로 이동' : 'Go to first page',
                last: locale.startsWith('ko') ? '마지막 페이지로 이동' : 'Go to last page'
            }
        };
    } else if (!vuetifyLocales[locale].pagination.ariaLabel) {
        vuetifyLocales[locale].pagination.ariaLabel = {
            root: locale.startsWith('ko') ? '페이지네이션 네비게이션' : 'Pagination Navigation',
            next: locale.startsWith('ko') ? '다음 페이지로 이동' : 'Go to next page',
            previous: locale.startsWith('ko') ? '이전 페이지로 이동' : 'Go to previous page',
            page: locale.startsWith('ko') ? '{0}페이지로 이동' : 'Go to page {0}',
            currentPage: locale.startsWith('ko') ? '{0}페이지' : 'Page {0}',
            first: locale.startsWith('ko') ? '첫 페이지로 이동' : 'Go to first page',
            last: locale.startsWith('ko') ? '마지막 페이지로 이동' : 'Go to last page'
        };
    }
});

const vuetify = createVuetify({
    components: {
        ...components,
        draggable: VueDraggableNext,
        VTimePicker,
        VTreeview
    },
    directives,

    theme: {
        defaultTheme: 'BLUE_THEME',
        themes: {
            BLUE_THEME,
            DARK_BLUE_THEME,
            DARK_AQUA_THEME,
            DARK_ORANGE_THEME,
            DARK_PURPLE_THEME,
            DARK_GREEN_THEME,
            DARK_CYAN_THEME
        }
    },
    defaults: {
        VCard: {
            rounded: 'md'
        },
        VTextField: {
            variant: 'outlined',
            density: 'comfortable',
            color: 'primary'
        },
        VTextarea: {
            variant: 'outlined',
            density: 'comfortable',
            color: 'primary'
        },
        VSelect: {
            variant: 'outlined',
            density: 'comfortable',
            color: 'primary'
        },
        VListItem: {
            minHeight: '45px'
        },
        VTooltip: {
            location: 'top'
        },
    },
    locale: {
        defaultLocale: 'en-US',
        fallbackLocale: 'en-US',
        messages: vuetifyLocales
    } as any
});

// 전역 vuetify 인스턴스 export (locale 동적 변경을 위해)
export { vuetify };

export default vuetify;
